/* eslint-disable no-unused-expressions */
import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {useDropzone} from 'react-dropzone';
//formik
import {FieldInputProps, FormikProps} from 'formik';
//mui
import {Box, SxProps, Theme, Typography} from '@mui/material';
//icons
import {ReactComponent as AddPhotoIcon} from '@/assets/icons/add-photo.svg';
import {ReactComponent as ImportFileIcon} from '@/assets/icons/import-file.svg';
//constants
import {BODY2, CHOOSE_FILE, ERROR, IMAGE_PREVIEW_ALT, PRIMARY, SPAN, TEXT} from '@/utils/constants';
//helpers
import {getImagePreview} from '@/utils/helpers';
//components
import Button from '../../Button';
import InputLabel from '../../Inputs/InputLabel';
import FormHelperText from '../../FormHelperText';
//styles
import {uploadImageStyle} from './UploadImage.styles';

interface UploadImageProps {
	field?: FieldInputProps<File>;
	form?: FormikProps<any>;
	label?: string;
	parentClassName?: SxProps<Theme>;
	image?: File | null | string;
	isImage?: boolean;
	customError?: string;
	disabled?: boolean;
	disableErrorMessage?: boolean;
	onChange?: (file: File) => void;
}

const UploadImage: FC<UploadImageProps> = ({
	field,
	form,
	label,
	disabled,
	customError,
	image,
	isImage,
	disableErrorMessage,
	parentClassName,
	onChange,
}) => {
	const [uploadedImage, setUploadedImage] = useState<string>();

	useEffect(() => {
		const formikValue = field?.value;

		if (image) {
			setUploadedImage(getImagePreview(image));
		} else if (formikValue) {
			setUploadedImage(getImagePreview(formikValue));
		} else {
			setUploadedImage(undefined);
		}
	}, [image, field?.value]);

	const onDrop = useCallback((acceptedFiles: File[]) => {
		const image = getImagePreview(acceptedFiles[0]);
		setUploadedImage(image);

		if (form && field) {
			form.setFieldValue(field.name, acceptedFiles[0]);
		} else if (onChange) {
			onChange(acceptedFiles[0]);
		}
	}, []);

	const {isDragAccept, getRootProps, open, getInputProps} = useDropzone({
		onDrop,
		noClick: true,
		noKeyboard: true,
		accept: {
			'image/*': ['.jpeg', '.png'],
		},
	});

	const errorText = useMemo(() => {
		if (customError) return customError;
		return (field?.name && form?.touched[field?.name] && (form?.errors[field?.name] as string)) || undefined;
	}, [form?.errors, form?.touched, field, customError]);

	return (
		<Box
			{...getRootProps()}
			sx={{
				...parentClassName,
				...uploadImageStyle.wrapper,
				...(isDragAccept && uploadImageStyle.dragged),
				...(disabled && uploadImageStyle.disabled),
				...(!!errorText && uploadImageStyle.error),
			}}
		>
			<input {...getInputProps()} />

			{label && <InputLabel label={label} error={!!errorText} disabled={disabled} />}

			{uploadedImage ? (
				<Box sx={uploadImageStyle.image}>
					<img src={uploadedImage} alt={IMAGE_PREVIEW_ALT} />
				</Box>
			) : (
				<>
					{isImage ? (
						<Box
							sx={{
								...uploadImageStyle.icon,
								...(!!errorText && uploadImageStyle.errorIcon),
								...(disabled && uploadImageStyle.disabledIcon),
							}}
						>
							<AddPhotoIcon />
						</Box>
					) : (
						<Box
							sx={{
								...uploadImageStyle.icon,
								...(!!errorText && uploadImageStyle.errorIcon),
								...(disabled && uploadImageStyle.disabledIcon),
							}}
						>
							<ImportFileIcon />
						</Box>
					)}

					<Typography
						sx={{
							...uploadImageStyle.title,
							...(!!errorText && uploadImageStyle.errorTitle),
							...(disabled && uploadImageStyle.disabledTitle),
						}}
						variant={BODY2}
					>
						Drag & Drop
						<Typography
							sx={{
								...uploadImageStyle.title,
								...(!!errorText && uploadImageStyle.errorTitle),
								...(!!disabled && uploadImageStyle.disabledTitle),
							}}
							variant={BODY2}
							component={SPAN}
						>
							or
							<Button
								sx={uploadImageStyle.button}
								onClick={open}
								variant={TEXT}
								title={CHOOSE_FILE}
								disabled={disabled}
								color={errorText ? ERROR : PRIMARY}
								disableRipple
							/>
							to upload
						</Typography>
					</Typography>
				</>
			)}

			{!disableErrorMessage && errorText && <FormHelperText error title={errorText} />}
		</Box>
	);
};

export default UploadImage;
